/**
 * Contracts of «Documenti in scadenza» on the 3.0.0 archive (checklist M 15, NEW-M15):
 *
 *  - `GET /api/v2/document-archive-docs/deadlines`: one page of the documents expiring from a day on;
 *  - `GET /api/v2/document-archive-docs/deadlines/export`: the same documents as an Excel file.
 *
 * They replace the legacy `GET /deadlines/documents`, `GET /deadlines/property/:id/documents` and
 * their two Excels, which read the other archive.
 *
 * Readers: `admin`, `operator`, `master` and `collaborator` only (403 for the guests and the taggers),
 * with `canReadDocumentsList` on the list and `canExportSubscriptions` on the Excel. `master` and
 * `collaborator` read the properties of the company of the request that the archive lets them open.
 *
 * Shapes copied from the back-end (`document-archive-deadline.interface.ts`, README of the module),
 * which is the source of truth.
 *
 * @since 3.0.0
 */

import { DocumentArchiveSearchRow } from "src/app/shared/interfaces/document-archive-search.interface";

/** Every value `sort` accepts: any other is answered 400 */
export type DocumentArchiveDeadlineSortKey =
  | "property"
  | "area"
  | "document"
  | "endDate"
  | "memoTime"
  | "renewalType"
  | "providerName"
  | "structureObjectName"
  | "createdAt"
  | "masterCode";

/** The «Tipo rinnovo» filter: the documents renewing by themselves (`tacit`), or the others (`false`) */
export type DocumentArchiveDeadlineRenewalFilter = "tacit" | "false";

/** Most rows one page may ask for: above it the back-end answers 400 */
export const ARCHIVE_DEADLINES_MAX_LIMIT = 998;

/** Longest text a text filter accepts, measured once trimmed: above it the back-end answers 400 */
export const ARCHIVE_DEADLINES_MAX_TEXT = 100;

/**
 * The filters, sent under `search[…]`. Every key is optional and the list is CLOSED: any other key,
 * `area` included, is answered 400. An empty value is no filter; the front-end omits it anyway.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDeadlineFilters {
  /** One property: the list of that property. A property out of the caller's reach lists nothing. */
  property?: string;
  /** First expiry day listed, `YYYY-MM-DD`, a Rome calendar day. The back-end's default is today. */
  endDate?: string;
  /** Text looked for inside the master code of the property, case ignored, taken literally */
  masterCode?: string;
  /** Text looked for inside the CURRENT name of the element the row hangs from */
  structureObjectName?: string;
  /** Text looked for inside the name of the supplier */
  providerName?: string;
  /**
   * Text looked for inside the ITALIAN label of the document type, or its title when it has none,
   * whatever the language of the page: the label the back-end also sorts the column by.
   */
  document?: string;
  /** Text looked for inside the notice period */
  memoTime?: string;
  renewalType?: DocumentArchiveDeadlineRenewalFilter;
  /**
   * Free text, looked for in the group of the area, the element, the supplier, the document type and
   * the notice period: never in the property, nor in the code of the type.
   */
  general?: string;
}

/**
 * The query of the list: the window, the order and the filters.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDeadlinesParams {
  /** Rows to skip, from 0 */
  start: number;
  /** Rows of the page, 1 to ARCHIVE_DEADLINES_MAX_LIMIT */
  limit: number;
  sort: DocumentArchiveDeadlineSortKey;
  sortVersus: "asc" | "desc";
  search?: DocumentArchiveDeadlineFilters;
}

/**
 * One page of the list. The rows have exactly the shape of the general search
 * (@see DocumentArchiveSearchRow); `totalCount` counts every row the filters select, all pages
 * included, and only rows the list shows.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDeadlinesResult {
  items: DocumentArchiveSearchRow[];
  totalCount: number;
}
